import {
  Button,
  Col,
  Descriptions,
  Row,
  Space,
  TableColumnType,
  Typography,
} from "antd";
import moment from "moment";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { getOMCustomerInfo } from "../../../../apis/OrderManagement/order";
import useModals from "../../../../hooks/useModals";
import ReservationDetail from "../../../../model/ReservationDetail";
import CsMemoModal from "../../../../pages/ReservationDetail/components/CsMemoModal";
import FormatFunction from "../../../../shared/utils/Fn";
import color from "../../../../theme/color";
import { VehicleRegistStatusTag } from "../Tags/VehicleRegistStatus";
import { getModelAllYr } from "../utils/modelYear";
import { CardTitle, CustomCard } from "./Card.styled";
import { DescriptionTitle } from "./Description.styled";
import { CustomTable } from "./Table.styeld";

interface ConsumerInfoProps {
  dataForm: ReservationDetail;
  isMaster?: boolean;
}
export const ConsumerInfo: React.FC<ConsumerInfoProps> = ({
  dataForm,
  isMaster,
}) => {
  const { openModal } = useModals();

  const { data: customerInfo } = useQuery({
    queryKey: ["OM", dataForm.repair?.repairNo, "OrderCustomerInfo"],
    queryFn: () => getOMCustomerInfo(dataForm.repair?.repairNo as string),
    enabled: !!dataForm.repair?.repairNo,
    refetchOnMount: "always",
  });

  const columns: TableColumnType<any>[] = [
    {
      title: "작성일시",
      dataIndex: "createdAt",
      key: "createdAt",
      width: "20%",
      align: "center",
      render: (value) =>
        value ? moment(value).format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "메모 내용",
      dataIndex: "orderMemo",
      key: "orderMemo",
      ellipsis: true,
    },
    {
      title: "상세",
      dataIndex: "id",
      key: "id",
      width: "10%",
      fixed: "right",
      align: "center",
      render: (value) => (
        <Button
          type="primary"
          ghost
          onClick={() => openModal(<CsMemoModal id={value} />)}
          size="small"
        >
          상세
        </Button>
      ),
    },
  ];

  /**
   * 타이어 전륜 후륜 같은지 체크
   */
  const oneSize = useMemo(
    () =>
      customerInfo?.vehicle?.frontTire?.trim() ===
      customerInfo?.vehicle?.rearTire?.trim(),
    [customerInfo]
  );

  /**
   * 제품상세페이지- 호환모델 연도 표기
   */
  const modelYears = useMemo(
    () => getModelAllYr(customerInfo?.vehicle),
    [customerInfo]
  );

  if (!dataForm.order) return <></>;

  /**
   * SM가 IE 사용중이라 구소스에 차량정보만 추가
   * - 혹시 api 조회도 안돨가봐  신규정보 || 기존정보 순으로 처리
   */
  return (
    <CustomCard title={<CardTitle>고객정보</CardTitle>}>
      <Descriptions column={2} colon={false} labelStyle={{ width: 140 }}>
        <Descriptions.Item label={<DescriptionTitle>고객명</DescriptionTitle>}>
          {dataForm?.order.consumerName || "-"}
        </Descriptions.Item>
        <Descriptions.Item
          label={<DescriptionTitle>휴대폰 번호</DescriptionTitle>}
        >
          {(dataForm?.order.consumerPhone &&
            FormatFunction.regPhoneNumber(dataForm?.order.consumerPhone)) ||
            "-"}
        </Descriptions.Item>

        <Descriptions.Item
          label={<DescriptionTitle>차량 등록 방법</DescriptionTitle>}
        >
          <VehicleRegistStatusTag
            value={customerInfo?.vehicle?.vehicleRegistrationType || "UNKNOWN"}
          />
        </Descriptions.Item>
        <Descriptions.Item
          label={<DescriptionTitle>차량번호</DescriptionTitle>}
        >
          {customerInfo?.vehicle?.vehicleNumber ||
            dataForm.order.customerVehicleNo ||
            "-"}
        </Descriptions.Item>
        <Descriptions.Item
          label={<DescriptionTitle>차량브랜드</DescriptionTitle>}
        >
          <Typography.Text>
            {customerInfo?.vehicle?.brand ||
              dataForm?.repair.vahicleBrand ||
              "-"}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item
          label={<DescriptionTitle>브랜드 타입</DescriptionTitle>}
        >
          <Typography.Text>
            {customerInfo?.vehicle?.isDomestic !== undefined ||
            dataForm.repair.isDomestic !== undefined
              ? customerInfo?.vehicle?.isDomestic || dataForm.repair.isDomestic
                ? "국산"
                : "수입"
              : "-"}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label={<DescriptionTitle>차종</DescriptionTitle>}>
          <Space size={4} align="center" wrap>
            <Typography.Text>
              {customerInfo?.vehicle?.vehicleDetailGroupName ||
                customerInfo?.vehicle?.displayVehicleModel ||
                dataForm?.order.vehicleModel ||
                "-"}
            </Typography.Text>
            {modelYears && (
              <Typography.Text style={{ color: color.gray5, fontSize: 13 }}>
                {modelYears}
              </Typography.Text>
            )}
          </Space>
        </Descriptions.Item>

        {!["SELECT"].includes(
          customerInfo?.vehicle?.vehicleRegistrationType || ""
        ) && (
          <Descriptions.Item
            label={<DescriptionTitle>상세모델</DescriptionTitle>}
          >
            <Space size={4} align="center" wrap>
              <Typography.Text>
                {customerInfo?.vehicle?.trim || "-"}
              </Typography.Text>
            </Space>
          </Descriptions.Item>
        )}
        {!["SELECT", "ADMIN"].includes(
          customerInfo?.vehicle?.vehicleRegistrationType || ""
        ) && (
          <Descriptions.Item
            label={<DescriptionTitle>배터리 규격</DescriptionTitle>}
          >
            {customerInfo?.vehicle?.batteryModel || "-"}
          </Descriptions.Item>
        )}
        {!["SELECT", "ADMIN"].includes(
          customerInfo?.vehicle?.vehicleRegistrationType || ""
        ) && (
          <>
            {oneSize && (
              <Descriptions.Item
                label={<DescriptionTitle>타이어 사이즈</DescriptionTitle>}
              >
                {customerInfo?.vehicle?.frontTire || "-"}
              </Descriptions.Item>
            )}
            {!oneSize && (
              <>
                <Descriptions.Item
                  label={
                    <DescriptionTitle>타이어 사이즈 (전륜)</DescriptionTitle>
                  }
                >
                  {customerInfo?.vehicle?.frontTire || "-"}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <DescriptionTitle>타이어 사이즈 (후륜)</DescriptionTitle>
                  }
                >
                  {customerInfo?.vehicle?.rearTire || "-"}
                </Descriptions.Item>
              </>
            )}
          </>
        )}
        {!["ADMIN"].includes(
          customerInfo?.vehicle?.vehicleRegistrationType || ""
        ) && (
          <Descriptions.Item
            label={<DescriptionTitle>엔진오일 용량</DescriptionTitle>}
          >
            {(customerInfo?.vehicle?.engineOilLiter && (
              <>{customerInfo?.vehicle?.engineOilLiter} &#8467;</>
            )) ||
              "-"}
          </Descriptions.Item>
        )}
        {!["ADMIN"].includes(
          customerInfo?.vehicle?.vehicleRegistrationType || ""
        ) && (
          <Descriptions.Item
            label={<DescriptionTitle>엔진오일 추천점도</DescriptionTitle>}
          >
            {customerInfo?.vehicle?.engineOilViscosity || "-"}
          </Descriptions.Item>
        )}
        <Descriptions.Item
          span={2}
          label={<DescriptionTitle>요청사항</DescriptionTitle>}
        >
          {dataForm?.order.orderMemo || "-"}
        </Descriptions.Item>
      </Descriptions>
      <Descriptions layout="vertical" colon={false}>
        <Descriptions.Item
          label={<DescriptionTitle>고객센터 메모</DescriptionTitle>}
          span={3}
        >
          <Row style={{ width: "100%" }}>
            <Col span={24}>
              <CustomCard bordered bodyStyle={{ padding: 0 }}>
                <CustomTable
                  columns={columns}
                  dataSource={dataForm.order.orderMemos || []}
                  pagination={false}
                />
              </CustomCard>
            </Col>
          </Row>
        </Descriptions.Item>
      </Descriptions>
    </CustomCard>
  );
};
